/**
 * TanStack Query hooks for self-serve account management:
 *   - `useUpdateProfile()` — PATCH /api/auth/me  (email + language)
 *   - `useChangePassword()` — POST /api/auth/change-password
 *   - `useDeleteAccount()` — DELETE /api/auth/me
 *
 * These all invalidate `["auth","me"]` so the AuthProvider sees the new
 * profile (or null after delete). On delete, the browser is redirected
 * to /login since the cookie is cleared server-side.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { fetchJson, postJson } from "./client";
import type {
  AuthMe,
  ChangePasswordInput,
  DeleteAccountInput,
  UpdateProfileInput,
} from "./types";

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProfileInput) => {
      const body = await fetchJson<{ ok: true; user: AuthMe }>(
        "/api/auth/me",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        },
      );
      return body.user;
    },
    onSuccess: (user) => {
      qc.setQueryData(["auth", "me"], user);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      postJson<{ ok: true }>("/api/auth/change-password", input),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteAccountInput) =>
      fetchJson<{ ok: true }>("/api/auth/me", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.setQueryData(["auth", "me"], null);
      qc.clear();
    },
  });
}
